import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, Send, Heart, MapPin, User, Mail, Phone, 
  MessageSquare, CheckCircle, Sparkles, TrendingUp, Package,
  Upload, X, Image, AlertCircle, FileText, Home, Hash,
  Clock, Camera, Loader2, Check
} from 'lucide-react';
import emailjs from '@emailjs/browser';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// EmailJS Configuration - Viva Impact
const EMAILJS_PUBLIC_KEY = 'C8wac_c7nYiQrIwGL';
const EMAILJS_SERVICE_ID = 'vivaimpact_service';
const EMAILJS_ADMIN_TEMPLATE = 'vivaimpact_nova_solicita';
const EMAILJS_CONFIRM_TEMPLATE = 'vivaimpact_confirmacao';
const ADMIN_EMAIL = 'vimpact300@gmail.com';

// Initialize EmailJS
emailjs.init(EMAILJS_PUBLIC_KEY);

const SolicitarDoacoesPremium = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [protocolo, setProtocolo] = useState('');
  const [errors, setErrors] = useState({});
  const [loadingCep, setLoadingCep] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  
  const [formData, setFormData] = useState({
    // Dados Pessoais
    nome: '',
    email: '',
    telefone: '',
    // Localização
    cep: '',
    endereco: '',
    numero: '',
    complemento: '',
    bairro: '',
    cidade: '',
    estado: '',
    // Detalhes
    tipoDoacao: [],
    quantidade: '',
    urgencia: 'normal',
    descricao: '',
    fotoUrl: '',
    // Termos
    declaracaoVerdade: false,
    autorizacaoImagem: null // null = não selecionado, true = autoriza, false = não autoriza
  });

  const estados = [
    { sigla: 'AC', nome: 'Acre' }, { sigla: 'AL', nome: 'Alagoas' },
    { sigla: 'AP', nome: 'Amapá' }, { sigla: 'AM', nome: 'Amazonas' },
    { sigla: 'BA', nome: 'Bahia' }, { sigla: 'CE', nome: 'Ceará' },
    { sigla: 'DF', nome: 'Distrito Federal' }, { sigla: 'ES', nome: 'Espírito Santo' },
    { sigla: 'GO', nome: 'Goiás' }, { sigla: 'MA', nome: 'Maranhão' },
    { sigla: 'MT', nome: 'Mato Grosso' }, { sigla: 'MS', nome: 'Mato Grosso do Sul' },
    { sigla: 'MG', nome: 'Minas Gerais' }, { sigla: 'PA', nome: 'Pará' },
    { sigla: 'PB', nome: 'Paraíba' }, { sigla: 'PR', nome: 'Paraná' },
    { sigla: 'PE', nome: 'Pernambuco' }, { sigla: 'PI', nome: 'Piauí' },
    { sigla: 'RJ', nome: 'Rio de Janeiro' }, { sigla: 'RN', nome: 'Rio Grande do Norte' },
    { sigla: 'RS', nome: 'Rio Grande do Sul' }, { sigla: 'RO', nome: 'Rondônia' },
    { sigla: 'RR', nome: 'Roraima' }, { sigla: 'SC', nome: 'Santa Catarina' },
    { sigla: 'SP', nome: 'São Paulo' }, { sigla: 'SE', nome: 'Sergipe' },
    { sigla: 'TO', nome: 'Tocantins' }
  ];

  const tiposDoacaoOptions = [
    { value: 'alimentos', label: 'Alimentos' },
    { value: 'roupas', label: 'Roupas' },
    { value: 'brinquedos', label: 'Brinquedos' },
    { value: 'materiais_escolares', label: 'Materiais Escolares' },
    { value: 'higiene', label: 'Produtos de Higiene' },
    { value: 'cestas_basicas', label: 'Cestas Básicas' },
    { value: 'medicamentos', label: 'Medicamentos' },
    { value: 'moveis', label: 'Móveis/Eletrodomésticos' },
    { value: 'outros', label: 'Outros' }
  ];

  const urgenciaOptions = [
    { value: 'baixa', label: 'Baixa (30+ dias)', color: 'text-emerald-400' },
    { value: 'normal', label: 'Normal (15-30 dias)', color: 'text-blue-400' },
    { value: 'alta', label: 'Alta (até 15 dias)', color: 'text-amber-400' },
    { value: 'urgente', label: 'Urgente (até 7 dias)', color: 'text-red-400' }
  ];

  // Máscaras
  const formatPhone = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 2) return `(${numbers}`;
    if (numbers.length <= 7) return `(${numbers.slice(0, 2)}) ${numbers.slice(2)}`;
    if (numbers.length <= 11) return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7)}`;
    return `(${numbers.slice(0, 2)}) ${numbers.slice(2, 7)}-${numbers.slice(7, 11)}`;
  };

  const formatCEP = (value) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 5) return numbers;
    return `${numbers.slice(0, 5)}-${numbers.slice(5, 8)}`;
  };

  // Busca CEP via ViaCEP
  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;

    setLoadingCep(true);
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await response.json();
      
      if (!data.erro) {
        setFormData(prev => ({
          ...prev,
          endereco: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          estado: data.uf || ''
        }));
      }
    } catch (error) {
      console.error('Erro ao buscar CEP:', error);
    } finally {
      setLoadingCep(false);
    }
  };

  // Calcular progresso por step
  const calculateStepProgress = (step) => {
    switch(step) {
      case 1: // Dados Pessoais
        const pessoais = ['nome', 'email', 'telefone'];
        const filledPessoais = pessoais.filter(f => formData[f]?.length > 0).length;
        return (filledPessoais / pessoais.length) * 100;
      case 2: // Localização
        const localizacao = ['cep', 'endereco', 'numero', 'cidade', 'estado'];
        const filledLocalizacao = localizacao.filter(f => formData[f]?.length > 0).length;
        return (filledLocalizacao / localizacao.length) * 100;
      case 3: // Detalhes
        const detalhes = ['tipoDoacao', 'quantidade', 'descricao'];
        let filledDetalhes = 0;
        if (formData.tipoDoacao.length > 0) filledDetalhes++;
        if (formData.quantidade) filledDetalhes++;
        if (formData.descricao?.length >= 50) filledDetalhes++;
        return (filledDetalhes / detalhes.length) * 100;
      case 4: // Termos
        return formData.declaracaoVerdade ? 100 : 0;
      default:
        return 0;
    }
  };

  const totalProgress = Math.round(
    (calculateStepProgress(1) + calculateStepProgress(2) + calculateStepProgress(3) + calculateStepProgress(4)) / 4
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    // Aplicar máscaras
    if (name === 'telefone') newValue = formatPhone(value);
    if (name === 'cep') {
      newValue = formatCEP(value);
      if (value.replace(/\D/g, '').length === 8) {
        buscarCEP(value);
      }
    }

    setFormData(prev => ({ ...prev, [name]: newValue }));
    
    // Limpar erro do campo
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleAutorizacaoChange = (autoriza) => {
    setFormData(prev => ({ ...prev, autorizacaoImagem: autoriza }));
    if (errors.autorizacaoImagem) {
      setErrors(prev => ({ ...prev, autorizacaoImagem: null }));
    }
  };

  const handleTipoDoacaoChange = (tipo) => {
    setFormData(prev => {
      const tipos = prev.tipoDoacao.includes(tipo)
        ? prev.tipoDoacao.filter(t => t !== tipo)
        : [...prev.tipoDoacao, tipo];
      return { ...prev, tipoDoacao: tipos };
    });
  };

  // Upload de imagem para Cloudinary
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validar tamanho (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, foto: 'Imagem deve ter no máximo 5MB' }));
      return;
    }

    // Validar tipo
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setErrors(prev => ({ ...prev, foto: 'Formato deve ser JPG, PNG ou WEBP' }));
      return;
    }

    // Preview local
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload para Cloudinary
    setUploadingImage(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('upload_preset', 'vivaimpact_uploads');
      formDataUpload.append('folder', 'doacoes_solicitacoes');

      const response = await fetch('https://api.cloudinary.com/v1_1/dnrowstdh/image/upload', {
        method: 'POST',
        body: formDataUpload
      });

      const data = await response.json();
      if (data.secure_url) {
        setFormData(prev => ({ ...prev, fotoUrl: data.secure_url }));
        setErrors(prev => ({ ...prev, foto: null }));
      }
    } catch (error) {
      console.error('Erro no upload:', error);
      setErrors(prev => ({ ...prev, foto: 'Erro ao enviar imagem. Tente novamente.' }));
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setImagePreview(null);
    setFormData(prev => ({ ...prev, fotoUrl: '' }));
  };

  // Validação
  const validateForm = () => {
    const newErrors = {};

    // Dados Pessoais
    if (!formData.nome.trim()) newErrors.nome = 'Nome é obrigatório';
    if (!formData.email.trim()) newErrors.email = 'E-mail é obrigatório';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'E-mail inválido';
    if (!formData.telefone.trim()) newErrors.telefone = 'Telefone é obrigatório';
    else if (formData.telefone.replace(/\D/g, '').length < 10) newErrors.telefone = 'Telefone inválido';

    // Localização
    if (!formData.cep.trim()) newErrors.cep = 'CEP é obrigatório';
    if (!formData.endereco.trim()) newErrors.endereco = 'Endereço é obrigatório';
    if (!formData.numero.trim()) newErrors.numero = 'Número é obrigatório';
    if (!formData.cidade.trim()) newErrors.cidade = 'Cidade é obrigatória';
    if (!formData.estado) newErrors.estado = 'Estado é obrigatório';

    // Detalhes
    if (formData.tipoDoacao.length === 0) newErrors.tipoDoacao = 'Selecione pelo menos um tipo';
    if (!formData.quantidade) newErrors.quantidade = 'Quantidade é obrigatória';
    if (!formData.descricao.trim()) newErrors.descricao = 'Descrição é obrigatória';
    else if (formData.descricao.length < 50) newErrors.descricao = 'Mínimo 50 caracteres';

    // Termos
    if (!formData.declaracaoVerdade) newErrors.declaracaoVerdade = 'Você deve aceitar a declaração';
    if (formData.autorizacaoImagem === null) newErrors.autorizacaoImagem = 'Você deve selecionar uma opção de autorização';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Gerar número de protocolo (formato: VI-YYYYMMDD-XXXX)
  const generateProtocolo = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const random = String(Math.floor(Math.random() * 10000)).padStart(4, '0');
    return `VI-${year}${month}${day}-${random}`;
  };

  // Envio do formulário
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      // Ir para o step com erro
      if (errors.nome || errors.email || errors.telefone) setCurrentStep(1);
      else if (errors.cep || errors.endereco || errors.numero || errors.cidade || errors.estado) setCurrentStep(2);
      else if (errors.tipoDoacao || errors.quantidade || errors.descricao) setCurrentStep(3);
      else setCurrentStep(4);
      return;
    }

    setIsSubmitting(true);
    
    // 1. Gerar protocolo
    const generatedProtocolo = generateProtocolo();
    setProtocolo(generatedProtocolo);
    
    // Formatar dados para emails
    const currentDate = new Date().toLocaleString('pt-BR');
    const tiposDoacaoFormatted = formData.tipoDoacao.map(t => {
      const labels = {
        alimentos: 'Alimentos', roupas: 'Roupas', brinquedos: 'Brinquedos',
        materiais_escolares: 'Mat. Escolares', higiene: 'Higiene',
        cestas_basicas: 'Cestas Básicas', medicamentos: 'Medicamentos',
        moveis: 'Móveis/Eletro', outros: 'Outros'
      };
      return labels[t] || t;
    }).join(', ');

    const urgenciaLabels = {
      baixa: 'Baixa (30+ dias)', normal: 'Normal (15-30 dias)',
      alta: 'Alta (até 15 dias)', urgente: 'URGENTE (até 7 dias)'
    };

    const autorizacaoTexto = formData.autorizacaoImagem 
      ? 'AUTORIZADO' 
      : 'NÃO AUTORIZADO';

    try {
      // 2. Enviar para o backend (API) - tenta mas não bloqueia
      try {
        await fetch(`${BACKEND_URL}/api/donations/request`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...formData,
            protocolo: generatedProtocolo,
            authorized_image: formData.autorizacaoImagem
          })
        });
        console.log('Solicitação salva no backend');
      } catch (backendError) {
        console.error('Erro ao salvar no backend (não crítico):', backendError);
      }

      // 3. Enviar email para ADMIN (vimpact300@gmail.com)
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_ADMIN_TEMPLATE,
          {
            to_email: ADMIN_EMAIL,
            protocolo: generatedProtocolo,
            data: currentDate,
            urgencia: urgenciaLabels[formData.urgencia] || formData.urgencia,
            nome: formData.nome,
            email: formData.email,
            telefone: formData.telefone,
            endereco: `${formData.endereco}, ${formData.numero}`,
            complemento: formData.complemento || '-',
            bairro: formData.bairro || '-',
            cidade: formData.cidade,
            estado: formData.estado,
            cep: formData.cep,
            tipo_doacao: tiposDoacaoFormatted,
            quantidade: formData.quantidade,
            descricao: formData.descricao,
            foto_url: formData.fotoUrl || 'Nenhuma foto enviada',
            autorizacao_imagem: autorizacaoTexto
          }
        );
        console.log('✅ Email para admin enviado com sucesso');
      } catch (emailError) {
        console.error('❌ Erro ao enviar email para admin:', emailError);
        // Não bloqueia - continua com o processo
      }

      // 4. Enviar email de CONFIRMAÇÃO para o solicitante
      try {
        await emailjs.send(
          EMAILJS_SERVICE_ID,
          EMAILJS_CONFIRM_TEMPLATE,
          {
            to_email: formData.email,
            protocolo: generatedProtocolo,
            data: currentDate,
            nome: formData.nome,
            email: formData.email,
            telefone: formData.telefone,
            tipo_doacao: tiposDoacaoFormatted,
            urgencia: urgenciaLabels[formData.urgencia] || formData.urgencia,
            cidade: formData.cidade,
            estado: formData.estado
          }
        );
        console.log('✅ Email de confirmação enviado com sucesso');
      } catch (emailError) {
        console.error('❌ Erro ao enviar email de confirmação:', emailError);
        // Não bloqueia - continua com o processo
      }

      // 5. Mostrar tela de sucesso (sempre, mesmo se emails falharem)
      setSubmitSuccess(true);
      
    } catch (error) {
      console.error('Erro geral:', error);
      // Mesmo com erro, mostra sucesso pois o protocolo foi gerado
      setSubmitSuccess(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Tela de Sucesso
  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-[#0a0f1e] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-8 max-w-md w-full text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
            className="w-24 h-24 mx-auto mb-6 rounded-full bg-[#22c55e]/20 flex items-center justify-center"
          >
            <CheckCircle className="w-12 h-12 text-[#22c55e]" />
          </motion.div>
          
          <h2 className="text-2xl font-bold text-white mb-2">Solicitação Enviada!</h2>
          <p className="text-slate-400 mb-6">Sua solicitação foi recebida com sucesso</p>
          
          <div className="bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-xl p-4 mb-6">
            <p className="text-[#22c55e] text-sm mb-1">Número do Protocolo</p>
            <p className="text-2xl font-mono font-bold text-white">{protocolo}</p>
          </div>
          
          <div className="bg-white/[0.02] border border-white/10 rounded-xl p-4 mb-6 text-left">
            <p className="text-slate-400 text-sm mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Um email de confirmação foi enviado para:
            </p>
            <p className="text-white font-medium">{formData.email}</p>
          </div>
          
          <p className="text-slate-400 text-sm mb-8">
            Nossa equipe entrará em contato em até <strong className="text-white">48 horas</strong> pelo WhatsApp ou e-mail informado.
          </p>
          
          <button
            onClick={() => navigate('/')}
            className="w-full py-3 bg-[#22c55e] text-[#0a0f1e] font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(34,197,94,0.4)] transition-all"
          >
            Voltar ao Início
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0f1e] relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#22c55e]/10 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-[#f59e0b]/10 rounded-full blur-[100px]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(34,197,94,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(34,197,94,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft size={20} />
            <span>Voltar</span>
          </button>

          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-full text-[#22c55e] text-sm mb-4">
              <Sparkles size={16} />
              <span>Transformando Vidas</span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Solicitar <span className="text-[#22c55e]">Doação</span>
            </h1>
            <p className="text-slate-400">
              Preencha o formulário e nossa equipe entrará em contato em até 48 horas
            </p>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-between max-w-2xl mx-auto mb-8">
            {[
              { num: 1, label: 'Dados Pessoais', icon: User },
              { num: 2, label: 'Localização', icon: MapPin },
              { num: 3, label: 'Detalhes', icon: Package },
              { num: 4, label: 'Confirmação', icon: Check }
            ].map((step, index) => (
              <React.Fragment key={step.num}>
                <button
                  onClick={() => setCurrentStep(step.num)}
                  className={`flex flex-col items-center gap-2 ${
                    currentStep === step.num ? 'text-[#22c55e]' : 
                    calculateStepProgress(step.num) === 100 ? 'text-[#22c55e]/60' : 'text-slate-500'
                  }`}
                >
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all ${
                    currentStep === step.num ? 'bg-[#22c55e] text-[#0a0f1e]' :
                    calculateStepProgress(step.num) === 100 ? 'bg-[#22c55e]/20 text-[#22c55e]' : 
                    'bg-white/5 text-slate-500'
                  }`}>
                    {calculateStepProgress(step.num) === 100 && currentStep !== step.num ? (
                      <Check size={20} />
                    ) : (
                      <step.icon size={20} />
                    )}
                  </div>
                  <span className="text-xs hidden md:block">{step.label}</span>
                </button>
                {index < 3 && (
                  <div className={`flex-1 h-1 mx-2 rounded-full ${
                    calculateStepProgress(step.num) === 100 ? 'bg-[#22c55e]/30' : 'bg-white/5'
                  }`}>
                    <div 
                      className="h-full bg-[#22c55e] rounded-full transition-all duration-500"
                      style={{ width: `${calculateStepProgress(step.num)}%` }}
                    />
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Total Progress */}
          <div className="bg-white/[0.02] backdrop-blur-sm border border-white/10 rounded-xl p-4 max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-2">
              <span className="text-slate-400 text-sm">Progresso Total</span>
              <span className="text-[#22c55e] font-semibold">{totalProgress}%</span>
            </div>
            <div className="h-2 bg-white/5 rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#22c55e] to-[#4ade80]"
                initial={{ width: 0 }}
                animate={{ width: `${totalProgress}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <AnimatePresence mode="wait">
            {/* Step 1: Dados Pessoais */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className="w-10 h-10 rounded-xl bg-[#22c55e]/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-[#22c55e]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Dados Pessoais</h3>
                    <p className="text-slate-400 text-sm">Informações básicas para contato</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Nome */}
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 text-sm mb-2">
                      Nome Completo <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="text"
                        name="nome"
                        value={formData.nome}
                        onChange={handleChange}
                        placeholder="Digite seu nome completo"
                        className={`w-full pl-12 pr-4 py-3 bg-white/[0.02] border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all ${
                          errors.nome ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                        }`}
                      />
                      {formData.nome && !errors.nome && (
                        <CheckCircle className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#22c55e]" />
                      )}
                    </div>
                    {errors.nome && <p className="text-red-400 text-xs mt-1">{errors.nome}</p>}
                  </div>

                  {/* Email */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">
                      E-mail <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="seu@email.com"
                        className={`w-full pl-12 pr-4 py-3 bg-white/[0.02] border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all ${
                          errors.email ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                        }`}
                      />
                    </div>
                    {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                  </div>

                  {/* Telefone */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">
                      Telefone/WhatsApp <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="tel"
                        name="telefone"
                        value={formData.telefone}
                        onChange={handleChange}
                        placeholder="(11) 99999-9999"
                        maxLength={16}
                        className={`w-full pl-12 pr-4 py-3 bg-white/[0.02] border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all ${
                          errors.telefone ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                        }`}
                      />
                    </div>
                    {errors.telefone && <p className="text-red-400 text-xs mt-1">{errors.telefone}</p>}
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="px-6 py-3 bg-[#22c55e] text-[#0a0f1e] font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all flex items-center gap-2"
                  >
                    Próximo
                    <ArrowLeft className="w-5 h-5 rotate-180" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 2: Localização */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className="w-10 h-10 rounded-xl bg-[#22c55e]/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#22c55e]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Localização</h3>
                    <p className="text-slate-400 text-sm">Onde a doação será entregue</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* CEP */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">
                      CEP <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="text"
                        name="cep"
                        value={formData.cep}
                        onChange={handleChange}
                        placeholder="00000-000"
                        maxLength={9}
                        className={`w-full pl-12 pr-4 py-3 bg-white/[0.02] border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all ${
                          errors.cep ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                        }`}
                      />
                      {loadingCep && <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#22c55e] animate-spin" />}
                    </div>
                    {errors.cep && <p className="text-red-400 text-xs mt-1">{errors.cep}</p>}
                  </div>

                  {/* Endereço */}
                  <div className="md:col-span-2">
                    <label className="block text-slate-400 text-sm mb-2">
                      Endereço <span className="text-red-400">*</span>
                    </label>
                    <div className="relative">
                      <Home className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                      <input
                        type="text"
                        name="endereco"
                        value={formData.endereco}
                        onChange={handleChange}
                        placeholder="Rua, Avenida..."
                        className={`w-full pl-12 pr-4 py-3 bg-white/[0.02] border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all ${
                          errors.endereco ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                        }`}
                      />
                    </div>
                    {errors.endereco && <p className="text-red-400 text-xs mt-1">{errors.endereco}</p>}
                  </div>

                  {/* Número */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">
                      Número <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="numero"
                      value={formData.numero}
                      onChange={handleChange}
                      placeholder="Nº"
                      className={`w-full px-4 py-3 bg-white/[0.02] border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all ${
                        errors.numero ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                      }`}
                    />
                    {errors.numero && <p className="text-red-400 text-xs mt-1">{errors.numero}</p>}
                  </div>

                  {/* Complemento */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Complemento</label>
                    <input
                      type="text"
                      name="complemento"
                      value={formData.complemento}
                      onChange={handleChange}
                      placeholder="Apto, Bloco..."
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none transition-all"
                    />
                  </div>

                  {/* Bairro */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">Bairro</label>
                    <input
                      type="text"
                      name="bairro"
                      value={formData.bairro}
                      onChange={handleChange}
                      placeholder="Bairro"
                      className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white placeholder-slate-500 focus:border-[#22c55e]/50 focus:outline-none transition-all"
                    />
                  </div>

                  {/* Cidade */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">
                      Cidade <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="cidade"
                      value={formData.cidade}
                      onChange={handleChange}
                      placeholder="Cidade"
                      className={`w-full px-4 py-3 bg-white/[0.02] border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all ${
                        errors.cidade ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                      }`}
                    />
                    {errors.cidade && <p className="text-red-400 text-xs mt-1">{errors.cidade}</p>}
                  </div>

                  {/* Estado */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">
                      Estado <span className="text-red-400">*</span>
                    </label>
                    <select
                      name="estado"
                      value={formData.estado}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 bg-white/[0.02] border rounded-xl text-white focus:outline-none transition-all ${
                        errors.estado ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                      }`}
                    >
                      <option value="" className="bg-[#0a0f1e]">Selecione</option>
                      {estados.map(e => (
                        <option key={e.sigla} value={e.sigla} className="bg-[#0a0f1e]">{e.nome}</option>
                      ))}
                    </select>
                    {errors.estado && <p className="text-red-400 text-xs mt-1">{errors.estado}</p>}
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(1)}
                    className="px-6 py-3 bg-white/5 text-slate-300 font-semibold rounded-xl border border-white/10 hover:bg-white/10 transition-all flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    className="px-6 py-3 bg-[#22c55e] text-[#0a0f1e] font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all flex items-center gap-2"
                  >
                    Próximo
                    <ArrowLeft className="w-5 h-5 rotate-180" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 3: Detalhes */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className="w-10 h-10 rounded-xl bg-[#22c55e]/20 flex items-center justify-center">
                    <Package className="w-5 h-5 text-[#22c55e]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Detalhes da Solicitação</h3>
                    <p className="text-slate-400 text-sm">Especifique o que você precisa</p>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Tipo de Doação */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-3">
                      Tipo de Doação <span className="text-red-400">*</span>
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {tiposDoacaoOptions.map(tipo => (
                        <button
                          key={tipo.value}
                          type="button"
                          onClick={() => handleTipoDoacaoChange(tipo.value)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            formData.tipoDoacao.includes(tipo.value)
                              ? 'bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/50'
                              : 'bg-white/5 text-slate-400 border border-white/10 hover:bg-white/10'
                          }`}
                        >
                          {tipo.label}
                        </button>
                      ))}
                    </div>
                    {errors.tipoDoacao && <p className="text-red-400 text-xs mt-2">{errors.tipoDoacao}</p>}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Quantidade */}
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">
                        Pessoas Beneficiadas <span className="text-red-400">*</span>
                      </label>
                      <select
                        name="quantidade"
                        value={formData.quantidade}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white/[0.02] border rounded-xl text-white focus:outline-none transition-all ${
                          errors.quantidade ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                        }`}
                      >
                        <option value="" className="bg-[#0a0f1e]">Selecione</option>
                        <option value="1-10" className="bg-[#0a0f1e]">1 a 10 pessoas</option>
                        <option value="11-50" className="bg-[#0a0f1e]">11 a 50 pessoas</option>
                        <option value="51-100" className="bg-[#0a0f1e]">51 a 100 pessoas</option>
                        <option value="101-200" className="bg-[#0a0f1e]">101 a 200 pessoas</option>
                        <option value="200+" className="bg-[#0a0f1e]">Mais de 200 pessoas</option>
                      </select>
                      {errors.quantidade && <p className="text-red-400 text-xs mt-1">{errors.quantidade}</p>}
                    </div>

                    {/* Urgência */}
                    <div>
                      <label className="block text-slate-400 text-sm mb-2">
                        Nível de Urgência <span className="text-red-400">*</span>
                      </label>
                      <select
                        name="urgencia"
                        value={formData.urgencia}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/[0.02] border border-white/10 rounded-xl text-white focus:border-[#22c55e]/50 focus:outline-none transition-all"
                      >
                        {urgenciaOptions.map(opt => (
                          <option key={opt.value} value={opt.value} className="bg-[#0a0f1e]">{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Descrição */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-slate-400 text-sm">
                        Descrição da Necessidade <span className="text-red-400">*</span>
                      </label>
                      <span className={`text-xs ${formData.descricao.length >= 50 ? 'text-[#22c55e]' : 'text-slate-500'}`}>
                        {formData.descricao.length}/50 mín.
                      </span>
                    </div>
                    <textarea
                      name="descricao"
                      value={formData.descricao}
                      onChange={handleChange}
                      placeholder="Descreva a situação e o motivo da solicitação (mínimo 50 caracteres)..."
                      rows={5}
                      className={`w-full px-4 py-3 bg-white/[0.02] border rounded-xl text-white placeholder-slate-500 focus:outline-none transition-all resize-none ${
                        errors.descricao ? 'border-red-500' : 'border-white/10 focus:border-[#22c55e]/50'
                      }`}
                    />
                    {errors.descricao && <p className="text-red-400 text-xs mt-1">{errors.descricao}</p>}
                  </div>

                  {/* Upload de Foto */}
                  <div>
                    <label className="block text-slate-400 text-sm mb-2">
                      Foto da Situação <span className="text-slate-500">(opcional, máx 5MB)</span>
                    </label>
                    
                    {!imagePreview ? (
                      <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-white/10 rounded-xl cursor-pointer hover:border-[#22c55e]/50 transition-all">
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp"
                          onChange={handleImageUpload}
                          className="hidden"
                        />
                        {uploadingImage ? (
                          <Loader2 className="w-8 h-8 text-[#22c55e] animate-spin" />
                        ) : (
                          <>
                            <Camera className="w-8 h-8 text-slate-500 mb-2" />
                            <span className="text-slate-500 text-sm">Clique para enviar uma foto</span>
                          </>
                        )}
                      </label>
                    ) : (
                      <div className="relative w-full h-40 rounded-xl overflow-hidden">
                        <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={removeImage}
                          className="absolute top-2 right-2 w-8 h-8 bg-red-500/80 rounded-full flex items-center justify-center text-white hover:bg-red-500"
                        >
                          <X size={16} />
                        </button>
                        {uploadingImage && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <Loader2 className="w-8 h-8 text-white animate-spin" />
                          </div>
                        )}
                      </div>
                    )}
                    {errors.foto && <p className="text-red-400 text-xs mt-1">{errors.foto}</p>}
                  </div>
                </div>

                <div className="flex justify-between mt-6">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    className="px-6 py-3 bg-white/5 text-slate-300 font-semibold rounded-xl border border-white/10 hover:bg-white/10 transition-all flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Anterior
                  </button>
                  <button
                    type="button"
                    onClick={() => setCurrentStep(4)}
                    className="px-6 py-3 bg-[#22c55e] text-[#0a0f1e] font-semibold rounded-xl hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] transition-all flex items-center gap-2"
                  >
                    Próximo
                    <ArrowLeft className="w-5 h-5 rotate-180" />
                  </button>
                </div>
              </motion.div>
            )}

            {/* Step 4: Confirmação */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6"
              >
                <div className="flex items-center gap-3 mb-6 pb-4 border-b border-white/10">
                  <div className="w-10 h-10 rounded-xl bg-[#22c55e]/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-[#22c55e]" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold">Confirmação</h3>
                    <p className="text-slate-400 text-sm">Revise e confirme sua solicitação</p>
                  </div>
                </div>

                {/* Resumo */}
                <div className="space-y-4 mb-6">
                  <div className="grid grid-cols-2 gap-4 p-4 bg-white/[0.02] rounded-xl">
                    <div>
                      <p className="text-slate-500 text-xs">Nome</p>
                      <p className="text-white">{formData.nome || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Telefone</p>
                      <p className="text-white">{formData.telefone || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Cidade</p>
                      <p className="text-white">{formData.cidade} - {formData.estado || '-'}</p>
                    </div>
                    <div>
                      <p className="text-slate-500 text-xs">Urgência</p>
                      <p className={urgenciaOptions.find(u => u.value === formData.urgencia)?.color || 'text-white'}>
                        {urgenciaOptions.find(u => u.value === formData.urgencia)?.label || '-'}
                      </p>
                    </div>
                  </div>

                  <div className="p-4 bg-white/[0.02] rounded-xl">
                    <p className="text-slate-500 text-xs mb-1">Tipos de Doação Solicitados</p>
                    <div className="flex flex-wrap gap-2">
                      {formData.tipoDoacao.map(t => (
                        <span key={t} className="px-2 py-1 bg-[#22c55e]/10 text-[#22c55e] text-xs rounded">
                          {tiposDoacaoOptions.find(opt => opt.value === t)?.label}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Autorização de Uso de Imagem e Voz */}
                <div className="mb-6 p-5 bg-white/[0.02] border border-white/10 rounded-xl">
                  <h4 className="text-white font-semibold mb-3 flex items-center gap-2">
                    <Camera className="w-5 h-5 text-[#f59e0b]" />
                    Autorização de Uso de Imagem e Voz
                  </h4>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">
                    Para a realização da análise e possível aprovação da solicitação de doação, informamos que todas as ações são registradas por meio de fotos e vídeos, com o objetivo de garantir transparência, credibilidade e prestação de contas das iniciativas realizadas pela Viva Impact em parceria com a WG.
                  </p>
                  <p className="text-slate-400 text-sm leading-relaxed mb-4">
                    Ao prosseguir com a solicitação, o responsável declara estar ciente e, desde já, poderá autorizar o uso de imagem e voz para fins institucionais, incluindo divulgação em redes sociais, site oficial e demais materiais de comunicação.
                  </p>
                  <p className="text-amber-400/80 text-sm leading-relaxed mb-5 p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <AlertCircle className="w-4 h-4 inline mr-2" />
                    Ressaltamos que a não autorização poderá inviabilizar a continuidade da análise e participação nas ações, uma vez que o registro das entregas faz parte dos princípios de transparência do projeto.
                  </p>
                  
                  <div className="space-y-3">
                    <button
                      type="button"
                      onClick={() => handleAutorizacaoChange(true)}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                        formData.autorizacaoImagem === true 
                          ? 'bg-[#22c55e]/20 border-2 border-[#22c55e]' 
                          : 'bg-white/[0.02] border border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.autorizacaoImagem === true ? 'border-[#22c55e] bg-[#22c55e]' : 'border-slate-500'
                      }`}>
                        {formData.autorizacaoImagem === true && <Check className="w-3 h-3 text-[#0a0f1e]" />}
                      </div>
                      <span className={`text-sm ${formData.autorizacaoImagem === true ? 'text-[#22c55e] font-medium' : 'text-slate-300'}`}>
                        Autorizo o uso de imagem e voz
                      </span>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => handleAutorizacaoChange(false)}
                      className={`w-full flex items-center gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                        formData.autorizacaoImagem === false 
                          ? 'bg-red-500/20 border-2 border-red-500' 
                          : 'bg-white/[0.02] border border-white/10 hover:border-white/20'
                      }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        formData.autorizacaoImagem === false ? 'border-red-500 bg-red-500' : 'border-slate-500'
                      }`}>
                        {formData.autorizacaoImagem === false && <X className="w-3 h-3 text-white" />}
                      </div>
                      <span className={`text-sm ${formData.autorizacaoImagem === false ? 'text-red-400 font-medium' : 'text-slate-300'}`}>
                        Não autorizo o uso de imagem e voz
                      </span>
                    </button>
                  </div>
                  {errors.autorizacaoImagem && <p className="text-red-400 text-xs mt-2">{errors.autorizacaoImagem}</p>}
                </div>

                {/* Termos */}
                <div className="space-y-4 mb-6">
                  <label className={`flex items-start gap-3 p-4 rounded-xl cursor-pointer transition-all ${
                    formData.declaracaoVerdade ? 'bg-[#22c55e]/10 border border-[#22c55e]/30' : 'bg-white/[0.02] border border-white/10'
                  }`}>
                    <input
                      type="checkbox"
                      name="declaracaoVerdade"
                      checked={formData.declaracaoVerdade}
                      onChange={handleChange}
                      className="mt-1"
                    />
                    <span className="text-slate-300 text-sm">
                      Declaro que as informações fornecidas são verdadeiras e me responsabilizo pela sua veracidade.
                      <span className="text-red-400"> *</span>
                    </span>
                  </label>
                  {errors.declaracaoVerdade && <p className="text-red-400 text-xs">{errors.declaracaoVerdade}</p>}
                </div>

                {errors.submit && (
                  <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/30 rounded-xl mb-6">
                    <AlertCircle className="w-5 h-5 text-red-400" />
                    <span className="text-red-400 text-sm">{errors.submit}</span>
                  </div>
                )}

                <div className="flex justify-between">
                  <button
                    type="button"
                    onClick={() => setCurrentStep(3)}
                    className="px-6 py-3 bg-white/5 text-slate-300 font-semibold rounded-xl border border-white/10 hover:bg-white/10 transition-all flex items-center gap-2"
                  >
                    <ArrowLeft className="w-5 h-5" />
                    Anterior
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting || !formData.declaracaoVerdade || formData.autorizacaoImagem === null}
                    className="px-8 py-3 bg-[#22c55e] text-[#0a0f1e] font-semibold rounded-xl hover:shadow-[0_0_30px_rgba(34,197,94,0.4)] disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Enviar Solicitação
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>

        {/* Footer Info */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {[
            { icon: Heart, text: 'Sua solicitação será analisada com cuidado e atenção' },
            { icon: Clock, text: 'Resposta em até 48 horas' },
            { icon: CheckCircle, text: 'Processo 100% transparente' }
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3 p-4 bg-white/[0.02] border border-white/10 rounded-xl">
              <item.icon className="w-5 h-5 text-[#22c55e]" />
              <span className="text-slate-400 text-sm">{item.text}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SolicitarDoacoesPremium;
